import React, {useEffect, useMemo} from 'react';
import DashboardLayout from "../../layouts/DashboardLayout";
import {useTranslation} from "react-i18next";
import StepPanel from "../../components/registration/StepPanel";
import useRegistrationFormStore from "../../store/registration-form";
import {useMutation, useQuery} from "react-query";
import {apollo} from "../../utils/apollo";
import {GET_MERCHANT_REGISTRATION_FIELDS, GET_REGISTRATION_COUNTRIES, UPDATE_REGISTRATION} from "../../utils/graphql";
import useAuthStore from "../../store/auth";
import RegistrationStringField from "../../components/registration/fields/RegistrationStringField";
import RegistrationFileField from "../../components/registration/fields/RegistrationFileField";
import RegistrationCountryField from "../../components/registration/fields/RegistrationCountryField";
import RegistrationBooleanField from "../../components/registration/fields/RegistrationBooleanField";
import {toast} from "react-toastify";
import {cn} from "../../utils/style";
import {BuildingLibraryIcon, DocumentTextIcon, UserIcon} from '@heroicons/react/24/outline';
import Button from "../../components/Button";
import useModalStore from "../../store/modal";
import ConfirmationModal from "../../components/ConfirmationModal";

function Registration() {
    const {t} = useTranslation();
    const {
        step, setStep, credentials, setCredentials, dynamicFormValue, setDynamicFormValue,
        setDynamicFormValues,
    } = useRegistrationFormStore()
    const {user, merchant} = useAuthStore();
    const {setModal, clearModal, otpValue, otpRequired, getOtpValue, isOtpRequired, hideModal} = useModalStore();

    // const fetchRegistration = useQuery({
    // 	queryKey: ['registration'],
    // 	queryFn: () => http.get("/merchant/registration", {
    // 		params: {}
    // 	}),
    // 	keepPreviousData: false
    // });
    // const records = fetchRegistration?.data?.data || null;
    const fetchCountries = useQuery({
        queryKey: 'registration_countries',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_REGISTRATION_COUNTRIES,
                variables: {},
            });
            return data?.getRegistrationCountries;
        },
        refetchOnWindowFocus: false
    });

    const fetchFields = useQuery({
        queryKey: 'merchant_registration_fields',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_REGISTRATION_FIELDS,
                variables: {
                    country: merchant?.country
                },
            });
            return data;
        },
        enabled: !!merchant?.country,
        refetchOnWindowFocus: false
    });

    const fields = useMemo(() => {
        return fetchFields?.data?.getRegistrationForm || null;
    }, [fetchFields]);

    useEffect(() => {
        const list = fetchFields?.data?.getMerchantRegistrationFields || null;
        if (list && !Object.keys(dynamicFormValue).length) {
            let dynValues = {};
            list?.forEach(item => {
                const key = item?.fieldDefinition?.fieldName || null;
                const val = item?.fieldValue || null;
                if (key && val) dynValues[key] = val;
            })
            if (Object.keys(dynValues).length) {
                setDynamicFormValues(dynValues);
            }
        }
    }, [fetchFields]);

    const updateRegistrationMutation = useMutation({
        mutationFn: async () => {
            const {data} = await apollo.mutate({
                mutation: UPDATE_REGISTRATION,
                variables: {
                    input: {
                        fields: JSON.stringify({country: merchant?.country, ...dynamicFormValue})
                    }
                },
            });
            return data?.updateRegistration;
        },
        onSuccess: (data) => {
            if (data && data.success) {
                toast.success('Registration has been updated successfully.');
            } else if (data && data.message) {
                toast.error(data.message)
            } else {
                toast.error('Please check the submitted information again!')
            }
        },
        // onError: ((error) => {
        //     if (error.graphQLErrors) {
        //         error.graphQLErrors.map((item) => console.log(item));
        //         toast.error('Something went wrong!')
        //     }
        // })
    })

    const submitRegistrationForm = async (e) => {
        e.preventDefault();
        updateRegistrationMutation.mutate();
        // setModal({
        //     isVisible: true,
        //     title: t('registration.confirm_title'),
        //     description: t('registration.confirm_description'),
        //     otpRequired: false,
        //     confirmText: t('registration.submit'),
        //     onConfirm: () => {
        //         // if (isOtpRequired() && !getOtpValue()) {
        //         //     toast.error('Please enter OTP to confirm action.');
        //         //     return;
        //         // }
        //         updateRegistrationMutation.mutate();
        //         // hideModal();
        //     },
        //     onCancel: () => {
        //         hideModal();
        //     },
        // });
    }

    return (
        <DashboardLayout>
            <ConfirmationModal/>
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-blue-500 shadow-sm">
                        <UserIcon className="h-6 w-6 text-white"/>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('registration.page_title') || 'Merchant Registration'}</h2>
                </div>
                <p className="text-gray-600 mt-1">{t('registration.page_description') || 'Complete the registration process to activate your merchant account.'}</p>
            </div>

            {(fetchFields.isLoading || fetchCountries.isLoading) && (
                <div className="flex justify-center items-center py-12">
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div
                                className="h-16 w-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 opacity-20 animate-ping"></div>
                        </div>
                        <div className="relative flex items-center justify-center">
                            <div
                                className="h-16 w-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center">
                                <DocumentTextIcon className="h-8 w-8 text-white"/>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {(fetchFields.isSuccess && fetchCountries) && (
                <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-8">
                    <div
                        className="px-6 py-4 bg-gradient-to-br from-green-500 to-blue-500 border-b border-gray-200 rounded-t-xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="p-2 rounded-lg bg-white/20">
                                    <DocumentTextIcon className="h-5 w-5 text-white"/>
                                </div>
                                <h3 className="text-lg font-medium text-white">{t('registration.steps.title') || 'Registration Steps'}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="p-6">
                        <StepPanel step={step + 1} onClick={value => setStep(value - 1)}/>
                    </div>
                </div>
            )}

            {(fetchFields.isSuccess && fetchCountries) && (
                <div>
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-8">
                        <div
                            className="px-6 py-4 bg-gradient-to-br from-green-500 to-blue-500 border-b border-gray-200 rounded-t-xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-white/20">
                                        {step === 0 && <UserIcon className="h-5 w-5 text-white"/>}
                                        {step === 1 && <BuildingLibraryIcon className="h-5 w-5 text-white"/>}
                                        {step === 2 && <DocumentTextIcon className="h-5 w-5 text-white"/>}
                                    </div>
                                    <h3 className="text-lg font-medium text-white">
                                        {step === 0 && (t('registration.steps.personal_info') || 'Personal Information')}
                                        {step === 1 && (t('registration.steps.business_info') || 'Business Information')}
                                        {step === 2 && (t('registration.steps.documents') || 'Documents')}
                                    </h3>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="bg-blue-100 text-blue-800 p-4 rounded-lg border border-blue-200 mb-6">
                                {step === 0 && (
                                    <p>{t('registration.steps.personal_info_description') || 'Please provide your personal information to complete the registration process. All fields marked with * are required.'}</p>
                                )}
                                {step === 1 && (
                                    <p>{t('registration.steps.business_info_description') || 'Please provide your business information. This information will be used for verification and payment processing.'}</p>
                                )}
                                {step === 2 && (
                                    <p>{t('registration.steps.documents_description') || 'Please upload the required documents. All documents must be clear, legible, and in PDF, JPG, or PNG format.'}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {fields?.map(field => {
                                    if (!field || step + 1 !== field.displayOnSection) return null;

                                    const renderField = () => {
                                        if (field.fieldType === 'string')
                                            return <RegistrationStringField key={field.id} field={field}
                                                                            show={true}
                                                                            get={dynamicFormValue}
                                                                            set={setDynamicFormValue}/>;
                                        if (field.fieldType === 'file')
                                            return <RegistrationFileField key={field.id} field={field}
                                                                          show={true}
                                                                          get={dynamicFormValue}
                                                                          set={setDynamicFormValue}/>;
                                        if (field.fieldType === 'country')
                                            return <RegistrationCountryField key={field.id} field={field}
                                                                             show={true}
                                                                             get={merchant?.country}
                                                                             set={null} readOnly={true}
                                                                             countries={fetchCountries?.data}/>;
                                        if (field.fieldType === 'boolean')
                                            return <RegistrationBooleanField key={field.id} field={field}
                                                                             show={true}
                                                                             get={dynamicFormValue}
                                                                             set={setDynamicFormValue}/>;
                                        return null;
                                    };

                                    return (
                                        <div key={field.id}
                                             className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            {renderField()}
                                            {field.description && (
                                                <p className="text-sm text-gray-500 mt-2">{field.description}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-4">
                        <Button
                            secondary={true}
                            className={cn("font-medium", {
                                'opacity-50 cursor-not-allowed': step === 0
                            })}
                            onClick={() => step > 0 && setStep(step - 1)}
                            disabled={step === 0}
                        >
                            {t('registration.previous') || 'Previous'}
                        </Button>

                        {step < 2 ? (
                            <Button
                                className="font-medium"
                                onClick={() => setStep(step + 1)}
                            >
                                {t('registration.next') || 'Next'}
                            </Button>
                        ) : (
                            <Button
                                className="font-medium bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600"
                                onClick={submitRegistrationForm}
                            >
                                {t('registration.submit') || 'Update Registration'}
                            </Button>
                        )}
                    </div>
                </div>
            )}

        </DashboardLayout>
    );
}

export default Registration;
